import React from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { PlusCircleIcon } from "@heroicons/react/24/outline"
import ContentBrowser from "aldoo-ra/CMS/browsers/content-browser"
import NewsletterAPI from "../api"

const NewsletterTemplates = () => {
  const [showAddDialog, setShowAddDialog] = React.useState(false)
  const [showRestoreDialog, setShowRestoreDialog] = React.useState(false)

  // Ensure new templates default to newsletter category
  const handlePreSaveFormatter = (content) => {
    return {
      ...content,
      category: "newsletter" // Always set category to newsletter
    }
  }

  // Check if template can be deleted
  const handlePreDeleteCheck = async (template) => {
    try {
      const templateId = template?.content?.id

      if (!templateId) {
        return { canDelete: true }
      }

      const response = await NewsletterAPI.checkTemplateUsage(templateId)

      if (!response || !response.success) {
        return {
          canDelete: false,
          message: 'Failed to check template usage',
          releases: []
        }
      }

      return {
        canDelete: response.canDelete,
        message: response.message,
        releases: response.releases || []
      }
    } catch (error) {
      console.error('Error checking template usage:', error)
      return {
        canDelete: false,
        message: 'Failed to check template usage',
        releases: []
      }
    }
  }

  return (
    <div className="p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <Typography 
            component="h1"
            variant="admin_h3"
          >
            Newsletter Templates
          </Typography>

          <div className="flex items-center space-x-3">
            <Button 
              variant="admin_secondary" 
              onClick={() => setShowRestoreDialog(true)}
            >
              Restore
            </Button>
            
            <Button
              variant="admin_primary"
              onClick={() => setShowAddDialog(true)}
            >
              <PlusCircleIcon className="w-5 h-5 mr-1" />
              Add Template
            </Button>
          </div>
        </div>

        {/* Newsletter-specific templates browser */}
        <ContentBrowser
          contentType="MailTemplate"
          contentTitle=""
          addButtonText=""
          allowTypeSelection={false}
          allowTypeBrowsing={false}
          showPreview={true}
          searchFields="content.description content.id content.name"
          additionalFilters={{ "content.category": "newsletter" }}
          defaultFieldValues={{ category: "newsletter" }}
          onPreSaveFormatter={handlePreSaveFormatter}
          onPreDeleteCheck={handlePreDeleteCheck}
          externalAddTrigger={showAddDialog}
          onAddDialogClose={() => setShowAddDialog(false)}
          externalRestoreTrigger={showRestoreDialog}
          onRestoreDialogClose={() => setShowRestoreDialog(false)}
          showRestoreButton={false}
          styleOverrides={{
            wrapper: "px-0",
            container: "relative w-full flex flex-col h-[85vh] justify-start bg-admin_paper rounded-3xl px-6 pt-6 pb-6",
            searchInput: "absolute top-4 right-4 text-admin_text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1"
          }}
          additionalColumns={[
            {
              id: "name",
              title: "Name",
              headerClassName: "w-[28%] xl:w-[30%]",
              render: (value, item) => (
                <span className="text-admin_text font-medium truncate block">{item?.content?.name || 'Unnamed'}</span>
              ),
            },
            {
              id: "description",
              title: "Description", 
              headerClassName: "w-[30%]",
              render: (value, item) => (
                <div className="truncate max-w-full">
                  <span className="text-admin_text_secondary text-sm truncate block">{item?.content?.description || 'No description'}</span>
                </div>
              ),
            },
            {
              id: "id",
              title: "Template ID",
              headerClassName: "w-[16%]",
              render: (value, item) => (
                <span className="text-admin_text font-mono text-xs truncate block">{item?.content?.id || 'No ID'}</span>
              ),
            },
          ]}
        />
      </div>
    </div>
  )
}

export default NewsletterTemplates